/**
 * Suppliers Actions - Redux Thunk Actions
 * Handles supplier-related API calls and state updates
 * 
 * Note: Underlying service is now ProfessionalsService
 */

import ProfessionalsService from '../../services/professionals.service';
import { formatIsraeliPhone } from '../../utils/formatters';

// Action Types
export const SUPPLIERS_ACTION_TYPES = {
  // Load Suppliers List
  LOAD_SUPPLIERS_REQUEST: 'LOAD_SUPPLIERS_REQUEST',
  LOAD_SUPPLIERS_SUCCESS: 'LOAD_SUPPLIERS_SUCCESS', 
  LOAD_SUPPLIERS_FAILURE: 'LOAD_SUPPLIERS_FAILURE',

  // Load Single Supplier
  LOAD_SUPPLIER_REQUEST: 'LOAD_SUPPLIER_REQUEST',
  LOAD_SUPPLIER_SUCCESS: 'LOAD_SUPPLIER_SUCCESS',
  LOAD_SUPPLIER_FAILURE: 'LOAD_SUPPLIER_FAILURE',

  // Create Supplier
  CREATE_SUPPLIER_REQUEST: 'CREATE_SUPPLIER_REQUEST',
  CREATE_SUPPLIER_SUCCESS: 'CREATE_SUPPLIER_SUCCESS',
  CREATE_SUPPLIER_FAILURE: 'CREATE_SUPPLIER_FAILURE',

  // Update Supplier
  UPDATE_SUPPLIER_REQUEST: 'UPDATE_SUPPLIER_REQUEST',
  UPDATE_SUPPLIER_SUCCESS: 'UPDATE_SUPPLIER_SUCCESS',
  UPDATE_SUPPLIER_FAILURE: 'UPDATE_SUPPLIER_FAILURE',

  // Delete Supplier
  DELETE_SUPPLIER_REQUEST: 'DELETE_SUPPLIER_REQUEST',
  DELETE_SUPPLIER_SUCCESS: 'DELETE_SUPPLIER_SUCCESS',
  DELETE_SUPPLIER_FAILURE: 'DELETE_SUPPLIER_FAILURE',

  // Filter Suppliers
  FILTER_SUPPLIERS_BY_CATEGORY: 'FILTER_SUPPLIERS_BY_CATEGORY',
  FILTER_SUPPLIERS_BY_STATUS: 'FILTER_SUPPLIERS_BY_STATUS',
  CLEAR_SUPPLIERS_FILTERS: 'CLEAR_SUPPLIERS_FILTERS',
  INVALIDATE_SUPPLIERS_CACHE: 'INVALIDATE_SUPPLIERS_CACHE', // New action to force refetch

  // UI Actions
  SET_CURRENT_SUPPLIER: 'SET_CURRENT_SUPPLIER',
  CLEAR_CURRENT_SUPPLIER: 'CLEAR_CURRENT_SUPPLIER',
  SET_SUPPLIERS_PAGE: 'SET_SUPPLIERS_PAGE',
  SET_SUPPLIERS_SEARCH: 'SET_SUPPLIERS_SEARCH'
};

// Action Creators

/**
 * Load all suppliers with pagination
 * @param {Object} params
 * @param {number} params.pageNumber - Page number
 * @param {number} params.pageSize - Items per page
 * @param {string} params.searchTerm - Search query
 * @returns {Function} Redux thunk function
 */
export const loadSuppliers = ({ pageNumber = 1, pageSize = 10, searchTerm = '' } = {}) => {
  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_REQUEST });

    try {
      const response = await ProfessionalsService.getAllProfessionals();
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_SUCCESS,
        payload: {
          ...response,
          searchTerm
        }
      });
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_FAILURE,
        payload: error.message || 'Failed to load suppliers'
      });
    }
  };
};

/**
 * Load supplier by ID
 * @param {number} supplierId - Supplier ID
 * @returns {Function} Redux thunk function
 */
export const loadSupplierById = (supplierId) => {
  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIER_REQUEST });

    try {
      const professional = await ProfessionalsService.getProfessionalById(supplierId);
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIER_SUCCESS,
        payload: professional
      });
      return professional;
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIER_FAILURE,
        payload: error.message || `Failed to load supplier ${supplierId}`
      });
      throw error;
    }
  };
};

/**
 * Create new supplier
 * @param {Object} supplierData - Supplier data
 * @returns {Function} Redux thunk function
 */
export const createSupplier = (supplierData, options = {}) => {
  // const { reloadList = true } = options;

  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.CREATE_SUPPLIER_REQUEST });

    try {
      const professional = await ProfessionalsService.createProfessional(supplierData);
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.CREATE_SUPPLIER_SUCCESS,
        payload: professional
      });
      
      // Invalidate cache so next view refresh fetches fresh data
      dispatch({ type: SUPPLIERS_ACTION_TYPES.INVALIDATE_SUPPLIERS_CACHE });
      
      return professional;
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.CREATE_SUPPLIER_FAILURE,
        payload: error.message || 'Failed to create supplier'
      });
      throw error;
    }
  };
};

/**
 * Update existing supplier
 * @param {number} supplierId - Supplier ID
 * @param {Object} supplierData - Updated supplier data
 * @returns {Function} Redux thunk function
 */
export const updateSupplier = (supplierId, supplierData, options = {}) => {
  // const { reloadList = true } = options;

  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.UPDATE_SUPPLIER_REQUEST });

    try {
      // payload for update requires professionalId inside. 
      // If the component passed supplierData without it, let's inject it to be safe.
      // We also strictly remove 'id' to prevent it from being sent to the API,
      // as the API expects 'professionalId' and might reject 'id'.
      
      const { id, ...dataWithoutId } = supplierData;
      const payload = { ...dataWithoutId, professionalId: supplierId };
      
      await ProfessionalsService.updateProfessional(payload);
      
      // Since update returns void, we can mock the updated entity or fetch it again if needed. 
      // Existing code returned payload: response.supplier. 
      // Let's assume the component doesn't strictly depend on the returned object properties being fresh from server immediately,
      // or we can reload the list which refreshes everything.
      
      // We will perform a fetch by ID just to be clean if needed, 
      // or simply rely on reloadList.
      // But preserving existing API shape: 
      
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.UPDATE_SUPPLIER_SUCCESS,
        payload: { ...supplierData, id: supplierId }
      });
      
      // Invalidate cache so next view refresh fetches fresh data
      dispatch({ type: SUPPLIERS_ACTION_TYPES.INVALIDATE_SUPPLIERS_CACHE });
      
      return { ...supplierData, id: supplierId };
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.UPDATE_SUPPLIER_FAILURE,
        payload: error.message || `Failed to update supplier ${supplierId}`
      });
      throw error;
    }
  };
};

/**
 * Delete supplier
 * @param {number} supplierId - Supplier ID
 * @returns {Function} Redux thunk function
 */
export const deleteSupplier = (supplierId, options = {}) => {
  // const { reloadList = true } = options;

  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.DELETE_SUPPLIER_REQUEST });

    try {
      await ProfessionalsService.deleteProfessional(supplierId);
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.DELETE_SUPPLIER_SUCCESS,
        payload: { supplierId, message: 'Deleted successfully' }
      });
      
      // Invalidate cache so next view refresh fetches fresh data
      dispatch({ type: SUPPLIERS_ACTION_TYPES.INVALIDATE_SUPPLIERS_CACHE });
      
      return { success: true };
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.DELETE_SUPPLIER_FAILURE,
        payload: error.message || `Failed to delete supplier ${supplierId}`
      });
      throw error;
    }
  };
};

/**
 * Filter suppliers by category
 * @param {string} category - Supplier category (ID or name)
 * @param {Object} params
 * @returns {Function} Redux thunk function
 */
export const filterSuppliersByCategory = (
  category,
  {
    pageNumber = 1,
    pageSize = 10,
    searchTerm = '',
    settlementIds,
    selectedSettlements,
    forceLoad = false
  } = {}
) => {
  return async (dispatch, getState) => {
    // Check if we already have the full list loaded
    const state = getState();
    // Use 'suppliersApp' instead of 'suppliers' based on rootReducer key
    const suppliersState = state.suppliersApp || state.suppliers; 
    const { allSuppliers } = suppliersState?.suppliersList || {};

    let itemsToFilter = [];

    // If we have data, use it. If not, fetch it.
    if (!forceLoad && allSuppliers && allSuppliers.length > 0) {
       itemsToFilter = [...allSuppliers];
    } else {
       dispatch({ type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_REQUEST });
       try {
         const response = await ProfessionalsService.getAllProfessionals();
         itemsToFilter = response.items || [];
         
         // Dispatch success to populate allSuppliers for next time
         dispatch({
            type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_SUCCESS,
            payload: { items: itemsToFilter, updateVisible: false, keepLoading: true }
         });
       } catch (error) {
         dispatch({
            type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_FAILURE,
            payload: error.message || 'Failed to load suppliers'
         });
         return; // Stop here if validation failed
       }
    }

    try {
      let filteredItems = itemsToFilter;
      
      // 1. Filter by Category (Profession Type)
      const targetCategory = Number(category);
      if (Number.isFinite(targetCategory)) {
          filteredItems = filteredItems.filter(p => p.professionType === targetCategory);
      }
      
      // 2. Filter by Search Term (a phone matches as shown, 050-1234567, or as stored, +972…)
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        // Digits are matched against phones only when the term is a phone number (not e.g. "dan123@mail.com").
        const looksLikePhone = /^[\d\s\-+()]+$/.test(searchTerm.trim());
        const termDigits = looksLikePhone ? searchTerm.replace(/\D/g, '') : '';
        filteredItems = filteredItems.filter(p => 
           (p.name && p.name.toLowerCase().includes(lower)) ||
           (p.email && p.email.toLowerCase().includes(lower)) ||
           (p.phone && p.phone.includes(lower)) ||
           (p.phone && termDigits.length >= 3 && formatIsraeliPhone(p.phone).replace(/\D/g, '').includes(termDigits))
        );
      }

      // 3. Filter by Settlement IDs
      const targetSettlements = (settlementIds || []).map(Number).filter(n => n > 0);
      if (targetSettlements.length > 0) {
          filteredItems = filteredItems.filter(p => 
              p.settlementIds.some(sid => targetSettlements.includes(sid))
          );
      }
      
      const totalCount = filteredItems.length;
      const safePageSize = pageSize && pageSize > 0 ? pageSize : 10;
      const totalPages = Math.ceil(totalCount / safePageSize) || 1;
      const safePageNumber = Math.max(1, Math.min(pageNumber, totalPages));

      const startIndex = (safePageNumber - 1) * safePageSize;
      const paginatedItems = filteredItems.slice(startIndex, startIndex + safePageSize);

      dispatch({
        type: SUPPLIERS_ACTION_TYPES.FILTER_SUPPLIERS_BY_CATEGORY,
        payload: {
          items: paginatedItems,
          totalCount,
          pageNumber: safePageNumber,
          pageSize: safePageSize,
          totalPages,
          hasNextPage: safePageNumber < totalPages,
          hasPreviousPage: safePageNumber > 1,
          
          filterType: 'category',
          filterValue: category,

          searchTerm,
          settlementIds,
          selectedSettlements
        }
      });
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_FAILURE,
        payload: error.message || `Failed to filter suppliers by category: ${category}`
      });
    }
  };
};

/**
 * Filter suppliers by status
 * @param {string} status - Supplier status
 * @param {Object} params
 * @param {number} params.pageNumber - Page number
 * @param {number} params.pageSize - Items per page
 * @param {string} params.searchTerm - Search query
 * @returns {Function} Redux thunk function
 */
export const filterSuppliersByStatus = (status, { pageNumber = 1, pageSize = 10, searchTerm = '' } = {}) => {
  return async (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_REQUEST });

    try {
      const response = await ProfessionalsService.getAllProfessionals();
      
      // Client-side filtering
      if (status) {
        response.items = response.items.filter(item => item.status === status);
      }

      if (searchTerm) {
        const lowerTerm = searchTerm.toLowerCase();
        response.items = response.items.filter(item => 
          (item.name && item.name.toLowerCase().includes(lowerTerm)) ||
          (item.email && item.email.toLowerCase().includes(lowerTerm)) ||
          (item.phone && item.phone.includes(lowerTerm))
        );
      }
      
      response.totalCount = response.items.length;
      
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.FILTER_SUPPLIERS_BY_STATUS,
        payload: { ...response, filterType: 'status', filterValue: status, searchTerm }
      });
    } catch (error) {
      dispatch({
        type: SUPPLIERS_ACTION_TYPES.LOAD_SUPPLIERS_FAILURE,
        payload: error.message || `Failed to filter suppliers by status: ${status}`
      });
    }
  };
};

/**
 * Clear all filters and reload suppliers
 * @returns {Function} Redux thunk function
 */
export const clearSuppliersFilters = () => {
  return (dispatch) => {
    dispatch({ type: SUPPLIERS_ACTION_TYPES.CLEAR_SUPPLIERS_FILTERS });
    dispatch(loadSuppliers());
  };
};

/**
 * Set current supplier for viewing/editing
 * @param {Object} supplier - Supplier object
 * @returns {Object} Redux action
 */
export const setCurrentSupplier = (supplier) => ({
  type: SUPPLIERS_ACTION_TYPES.SET_CURRENT_SUPPLIER,
  payload: supplier
});

/**
 * Clear current supplier
 * @returns {Object} Redux action
 */
export const clearCurrentSupplier = () => ({
  type: SUPPLIERS_ACTION_TYPES.CLEAR_CURRENT_SUPPLIER
});

/**
 * Set current page for pagination
 * @param {number} page - Page number
 * @returns {Object} Redux action
 */
export const setSuppliersPage = (page) => ({
  type: SUPPLIERS_ACTION_TYPES.SET_SUPPLIERS_PAGE,
  payload: page
});

/**
 * Set search term for filtering
 * @param {string} searchTerm - Search term
 * @returns {Object} Redux action
 */
export const setSuppliersSearch = (searchTerm) => ({
  type: SUPPLIERS_ACTION_TYPES.SET_SUPPLIERS_SEARCH,
  payload: searchTerm
});